/**
 * WhatsAppHeaderButton — R46 slice, tightened in R51.
 *
 * A tiny header trigger for the FloatingWaWidget with an unread-count
 * badge. Polls `/wa/unread-count` every 10s (R51 — was 30s) and refreshes
 * on any `acq:wa-unread-refresh` event so mark-read + new-message flows
 * both update the badge without waiting for the next tick.
 */
import { useCallback, useEffect, useState } from "react";
import { MessageCircle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { openFloatingWa } from "@/components/wa/FloatingWaWidget";
import { pollWhenVisible } from "@/lib/pollWhenVisible";

// Feb 2026 R281 · Bumped from 10s → 30s. Header badge doesn't need
// sub-half-minute freshness; event-driven refresh handles the interactive
// case (mark-read, widget-close, new-inbound toast).
const POLL_MS = 30_000;

export default function WhatsAppHeaderButton() {
  const { activeWorkspaceId } = useAuth();
  const [unread, setUnread] = useState(0);
  // Iter · Feb 2026 — Users without WhatsApp permission were causing
  // /wa/unread-count 403 spam every 10s forever. Latch on the first
  // 403 and skip subsequent polls (still allows event-driven refresh).
  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(async () => {
    if (!activeWorkspaceId || forbidden) return;
    try {
      const { data } = await api.get(
        `/workspaces/${activeWorkspaceId}/wa/unread-count`
      );
      setUnread(Number(data?.unread || 0));
    } catch (e) {
      if (e?.response?.status === 403) setForbidden(true);
      // silent — header widget shouldn't spam errors
    }
  }, [activeWorkspaceId, forbidden]);

  useEffect(() => {
    if (forbidden) return undefined;
    load();
    // Feb 2026 R281 · Visibility-gated polling.
    return pollWhenVisible(load, POLL_MS);
  }, [load, forbidden]);

  // R51 · Refresh count on any WA state change — widget close, thread
  // mark-read, or a new inbound arriving (surfaced by the toast poller).
  useEffect(() => {
    const refresh = () => setTimeout(load, 400);
    // R51.1 · Optimistic instant decrement when a thread is opened —
    // the poller re-fetches 400ms later to reconcile with the server.
    const onThreadRead = (e) => {
      const n = Number(e?.detail?.prev_unread || 0);
      if (n > 0) setUnread((u) => Math.max(0, u - n));
    };
    window.addEventListener("acq:wa-widget-close", refresh);
    window.addEventListener("acq:wa-unread-refresh", refresh);
    window.addEventListener("acq:wa-thread-read", onThreadRead);
    return () => {
      window.removeEventListener("acq:wa-widget-close", refresh);
      window.removeEventListener("acq:wa-unread-refresh", refresh);
      window.removeEventListener("acq:wa-thread-read", onThreadRead);
    };
  }, [load]);

  return forbidden ? null : (
    <button
      onClick={() => openFloatingWa()}
      className="relative p-1.5 border border-border hover:border-foreground transition-colors"
      title="WhatsApp Inbox"
      data-testid="header-whatsapp-btn"
    >
      <MessageCircle className="w-4 h-4 text-emerald-600" />
      {unread > 0 && (
        <span
          className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 bg-rose-600 text-white text-[9px] leading-none font-bold flex items-center justify-center rounded-full"
          data-testid="header-whatsapp-unread"
        >
          {unread > 99 ? "99+" : unread}
        </span>
      )}
    </button>
  );
}
